/**
 * CASL Ability Plugin
 *
 * This plugin provides CASL-based authorization for the Vue application.
 * It creates an Ability instance that is updated when user data changes,
 * and provides $can/$cannot methods to the Vue app for permission checking.
 *
 * The ability rules are built from the permission system via /api/me endpoint.
 */

import type { PermissionMap } from '#shared/types/api'
import { createMongoAbility } from '@casl/ability'
import { abilitiesPlugin } from '@casl/vue'

// Define ability types for CASL
export type AppAbility = ReturnType<typeof createMongoAbility>

// Create default ability (empty rules - no permissions)
const initialAbility = createMongoAbility([])

/**
 * Build CASL ability rules from permission map (client-side version)
 * This mirrors the server-side buildAbilityRulesFromPermissions function
 */
function buildAbilityRules(
  permissions: PermissionMap | null | undefined,
  roles: Array<{ name: string }> | string[] = [],
) {
  if (!permissions)
    return []

  const rules: Array<{ action: string, subject: string }> = []

  const roleNames = roles
    .map(role => (typeof role === 'string' ? role : role.name))
    .filter((role): role is string => Boolean(role))

  // Check if user has godmode role - gets full access
  if (roleNames.includes('godmode'))
    return [{ action: 'manage', subject: 'all' }]

  // Check for system:manage permission (admin level)
  if (permissions.system?.manage)
    return [{ action: 'manage', subject: 'all' }]

  // Map resource permissions to CASL subjects
  // Subject names should be PascalCase for CASL convention
  const resourceToSubject: Record<string, string> = {
    admin_panel: 'AdminPanel',
    blog: 'Blog',
    camps: 'Camps',
    bookings: 'Bookings',
    finance: 'Finance',
    users: 'Users',
    roles: 'Roles',
    monitors: 'Monitors',
    forms: 'Forms',
    form_responses: 'FormResponses',
    reports: 'Reports',
    tickets: 'Tickets',
    participants: 'Participants',
    attendance: 'Attendance',
    participant_tags: 'ParticipantTags',
    events: 'Events',
    system: 'System',
    email_queue: 'EmailQueue',
    logs: 'Logs',
    content: 'Content',
    tags: 'Tags',
    coupons: 'Coupons',
    faqs: 'Faqs',
    feest_feeds: 'FeestFeeds',
    analytics: 'Analytics',
    settings: 'Settings',
    contact_messages: 'ContactMessages',
    maintenance: 'Maintenance',
    organizations: 'Organizations',
    payment_splits: 'PaymentSplits',
    periods: 'Periods',
    stripe_events: 'StripeEvents',
  }

  // Convert permissions to CASL rules
  for (const [resource, actions] of Object.entries(permissions)) {
    if (!actions || typeof actions !== 'object')
      continue

    const subject
      = resourceToSubject[resource]
        || resource.charAt(0).toUpperCase() + resource.slice(1)

    // If user has 'manage' action, give full CRUD
    if (actions.manage) {
      rules.push({ action: 'manage', subject })
      continue
    }

    // Otherwise, add specific actions
    for (const [action, hasPermission] of Object.entries(actions)) {
      if (hasPermission)
        rules.push({ action, subject })
    }
  }

  // Check for admin panel access
  const hasAdminPanelAccess = Boolean(
    permissions.admin_panel?.access
    || permissions.system?.read
    || permissions.system?.manage
    || Object.keys(permissions).length > 0,
  )

  if (hasAdminPanelAccess)
    rules.push({ action: 'access', subject: 'AdminPanel' })

  // Default: at least authenticated
  if (rules.length === 0)
    rules.push({ action: 'read', subject: 'Auth' })

  return rules
}

export default defineNuxtPlugin(nuxtApp => {
  // Install CASL Vue plugin with initial (empty) ability
  nuxtApp.vueApp.use(abilitiesPlugin, initialAbility, {
    useGlobalProperties: true, // This provides $can and $cannot globally
  })

  // Watch for user data changes and update ability rules
  const userData = useState<any>('auth-user-data', () => null)

  // Update ability when user data changes
  watch(
    () => userData.value,
    newUserData => {
      if (newUserData) {
        // Build rules from new permission system
        const permissions = (newUserData.permissions as PermissionMap) || {}
        const roles = newUserData.roles || []
        const rules = buildAbilityRules(permissions, roles)

        // Update the ability with new rules
        initialAbility.update(rules)
      }
      else {
        // Clear ability when user logs out
        initialAbility.update([])
      }
    },
    { immediate: true, deep: true },
  )
})
